import { useQueryClient } from "@tanstack/react-query";
import { ActionButton, RowActions } from "@zfp-akademie-a-s/zfp-design";
import { BlockedIcon, CheckCircleIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { InProgressIcon } from "Components/_Shared/Icons/Icons";
import { getListAnalysesOverviewQueryKey, useCloneAnalysis, useSetAnalysisState } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { toast } from "sonner";

type Props = {
  readonly analysisId: string;
  readonly versionId: string;
  readonly state: AnalysisDetailState;
};

export const AnalysesTableActionsCell: React.FC<Props> = ({ analysisId, versionId, state }) => {
  const { t } = useResource();
  const queryClient = useQueryClient();

  const { mutateAsync: createAnalysisVersion, isPending: isCloning } = useCloneAnalysis({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListAnalysesOverviewQueryKey() });
        toast.success(t(Resources.AnalysesListing.Toast.NewVersion));
      },
    },
  });
  const { mutateAsync: setAnalysisState, isPending: isSettingState } = useSetAnalysisState({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListAnalysesOverviewQueryKey() });
        toast.success(t(Resources.AnalysesListing.Toast.NewState));
      },
    },
  });

  const isMutating = isCloning || isSettingState;

  const handleStateChange = async (newState: AnalysisDetailState) => {
    if (isMutating) return;

    if (newState === AnalysisDetailState.in_progress) {
      await createAnalysisVersion({
        analysisId,
        versionId,
      });

      return;
    }

    await setAnalysisState({
      analysisId,
      versionId,
      data: {
        state: newState,
      },
    });
  };

  return (
    <RowActions>
      {state === AnalysisDetailState.in_progress && (
        <>
          <ActionButton
            label={t(Resources.AnalysesListing.Status.Completed)}
            icon={<CheckCircleIcon />}
            tone="success"
            disabled={isMutating}
            onClick={() => handleStateChange(AnalysisDetailState.completed)}
          />
          <ActionButton
            label={t(Resources.AnalysesListing.Status.Declined)}
            icon={<BlockedIcon />}
            disabled={isMutating}
            onClick={() => handleStateChange(AnalysisDetailState.declined)}
          />
        </>
      )}
      {([AnalysisDetailState.completed, AnalysisDetailState.declined] as AnalysisDetailState[]).includes(state) && (
        <ActionButton
          label={t(Resources.AnalysesListing.Status.InProgress)}
          icon={<InProgressIcon />}
          disabled={isMutating}
          onClick={() => handleStateChange(AnalysisDetailState.in_progress)}
        />
      )}
    </RowActions>
  );
};
